/**
 * Roads & Sewers-only counterpart of `dynamicBillSummarySerialization.ts`'s
 * `getRawDynamicBillSummaryTabItems`/`deserializeDynamicBillSummaryTabs`.
 *
 * Kept in its own file, rather than adding Roads & Sewers' fixed summary keys
 * into the shared `dynamicBillSummarySerialization.ts`'s own `FIXED_SUMMARY_KEYS`
 * set, so that touching Roads & Sewers' Dynamic Bill Summary support can never
 * change `/groundworks/review`'s behaviour — that page (`GroundworksReview.tsx`)
 * only ever calls the shared file's own `getRawDynamicBillSummaryTabItems`/
 * `deserializeDynamicBillSummaryTabs`, which this file never modifies.
 *
 * The node-tree helpers that don't depend on a domain's fixed-key list at all
 * (`serializeDynamicBillSummaryTabs`, `computeDynamicBillSummaryNodeAmount`,
 * `flattenDynamicBillSummaryLineItems`, the `DynamicBillSummaryLineItem` type)
 * are genuinely domain-agnostic and are re-exported from here so every Roads &
 * Sewers call site can import everything it needs from this one file instead
 * of reaching into the shared one directly.
 */
import type { DynamicBillSummaryNode, DynamicBillSummaryTab } from "../types/tender.types";
import {
  serializeDynamicBillSummaryTabs,
  computeDynamicBillSummaryNodeAmount,
  flattenDynamicBillSummaryLineItems,
  splitResourceCode,
  type DynamicBillSummaryLineItem,
} from "./dynamicBillSummarySerialization";

export {
  serializeDynamicBillSummaryTabs,
  computeDynamicBillSummaryNodeAmount,
  flattenDynamicBillSummaryLineItems,
};
export type { DynamicBillSummaryLineItem };

// The fixed domain keys `computeRoadsAndSewersBoqSummary` (roadsAndSewersBoqSummaryComputer.ts)
// always writes to `roadAndSewers.summary` (RoadsAndSewersBoqSummary) — never
// treated as a Dynamic Bill Summary tab even if their shape happened to match one.
const FIXED_ROADS_AND_SEWERS_SUMMARY_KEYS = new Set([
  "foulSewers",
  "pumpStation",
  "risingMain",
  "stormSewers",
  "pondsSwales",
  "cellularAttenuation",
  "weholiteAttenuation",
  "concAttenuation",
  "boxCulverts",
  "roads",
  "mainServices",
  "substation",
  "pos",
  "prelims",
  "compound",
]);

const isDynamicBillSummaryLineItemArray = (value: unknown): value is DynamicBillSummaryLineItem[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      !!item &&
      typeof item === "object" &&
      typeof (item as Record<string, unknown>).key === "string" &&
      typeof (item as Record<string, unknown>).description === "string" &&
      typeof (item as Record<string, unknown>).amount === "number",
  );

/** Reverse of `dynamicBillSummaryTabKey` (dynamicBillSummarySerialization.ts) —
 *  since the persisted format has no separate label field, the tab's display
 *  label is rebuilt from its own summary key ("extraPreliminaries" -> "Extra
 *  Preliminaries"). Duplicated from the shared file rather than imported since
 *  it isn't exported there. */
const humanizeRoadsAndSewersDynamicBillSummaryKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const lineItemToNode = (item: DynamicBillSummaryLineItem): DynamicBillSummaryNode => {
  if (Array.isArray(item.children)) {
    return {
      id: item.key,
      type: "section",
      label: item.description,
      children: item.children.map(lineItemToNode),
    };
  }
  const resourceCodes = splitResourceCode(item.resourceCode) ?? item.resourceCodes;
  return {
    id: item.key,
    type: "item",
    description: item.description,
    qty: item.quantity,
    unit: item.unit,
    rate: item.rate,
    amountType: item.amountType,
    amountText: item.amountText,
    ...(resourceCodes ? { resourceCodes } : { resourceCode: item.resourceCode }),
    plotMapping: item.plotMapping,
    runMatrixMapping: item.runMatrixMapping,
  };
};

/**
 * Same tab-key filtering as `deserializeRoadsAndSewersDynamicBillSummaryTabs`,
 * but returns the raw `DynamicBillSummaryLineItem[]` per tab key instead of
 * converting to `DynamicBillSummaryNode` — the raw shape still carries the
 * `amount` that was actually persisted, which the SummaryKey column needs to
 * compare against the live-recomputed amount.
 */
export const getRawRoadsAndSewersDynamicBillSummaryTabItems = (
  summary: Record<string, unknown> | undefined | null,
): Record<string, DynamicBillSummaryLineItem[]> => {
  if (!summary) return {};
  const out: Record<string, DynamicBillSummaryLineItem[]> = {};
  Object.entries(summary).forEach(([key, value]) => {
    if (!FIXED_ROADS_AND_SEWERS_SUMMARY_KEYS.has(key) && isDynamicBillSummaryLineItemArray(value)) {
      out[key] = value as DynamicBillSummaryLineItem[];
    }
  });
  return out;
};

/**
 * Reverse of `serializeDynamicBillSummaryTabs` for `roadAndSewers.summary` —
 * reconstructs the tab array from a fetched `roadAndSewers.summary` object by
 * picking out every entry that looks like a Dynamic Bill Summary line-item
 * array, skipping Roads & Sewers' own fixed domain keys. The tab's `id`/label
 * are rebuilt from its summary key, since the persisted format carries no
 * separate metadata for them.
 */
export const deserializeRoadsAndSewersDynamicBillSummaryTabs = (
  summary: Record<string, unknown> | undefined | null,
): DynamicBillSummaryTab[] => {
  if (!summary) return [];
  return Object.entries(summary)
    .filter(([key, value]) => !FIXED_ROADS_AND_SEWERS_SUMMARY_KEYS.has(key) && isDynamicBillSummaryLineItemArray(value))
    .map(([key, value]) => ({
      id: key,
      label: humanizeRoadsAndSewersDynamicBillSummaryKey(key),
      nodes: (value as DynamicBillSummaryLineItem[]).map(lineItemToNode),
    }));
};
